import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Sparkles, Compass, BookOpen, Home, PawPrint, X, BoxIcon } from 'lucide-react';
type Product = {
  name: string;
  priceText: string;
  Icon: BoxIcon;
  weight: number;
};
type Booking = {
  id: number;
  name: string;
  city: string;
  product: Product;
};
// Fixed catalog — the 5 real sessions offered on the site
const PRODUCTS: Product[] = [{
  name: 'Depuración Energética Esencial',
  priceText: '$70.000 ARS',
  Icon: Sparkles,
  weight: 28
}, {
  name: 'Depuración + Investigación Radiestésica',
  priceText: '$115.000 ARS',
  Icon: Compass,
  weight: 32
}, {
  name: 'Depuración + Investigación + Registros Akáshicos',
  priceText: '$160.000 ARS',
  Icon: BookOpen,
  weight: 18
}, {
  name: 'Limpieza energética de espacios',
  priceText: '$55.000 ARS',
  Icon: Home,
  weight: 11
}, {
  name: 'Limpieza energética de animales',
  priceText: '$60.000 ARS',
  Icon: PawPrint,
  weight: 11
}];
const NAMES: string[] = ['María', 'Lucía', 'Sofía', 'Valentina', 'Camila', 'Martina', 'Florencia', 'Agustina', 'Catalina', 'Ana', 'Julieta', 'Carolina', 'Daniela', 'Paula', 'Renata', 'Victoria', 'Isabella', 'Antonella', 'Josefina', 'Macarena', 'Constanza', 'Romina', 'Belén', 'Pilar', 'Mateo', 'Joaquín', 'Lucas', 'Tomás', 'Benjamín', 'Santiago', 'Nicolás', 'Federico', 'Bruno', 'Manuel', 'Diego', 'Sebastián', 'Gabriel', 'Andrés', 'Maximiliano', 'Lautaro'];
const CITIES: string[] = ['Buenos Aires', 'Córdoba', 'Mendoza', 'Rosario', 'La Plata', 'Tucumán', 'Mar del Plata', 'Salta', 'Bariloche', 'Neuquén', 'Santiago', 'Valparaíso', 'Madrid', 'Barcelona', 'Sevilla', 'Valencia', 'Lima', 'Cusco', 'Bogotá', 'Medellín', 'Cali', 'Ciudad de México', 'Guadalajara', 'Monterrey', 'Montevideo', 'Punta del Este', 'Quito', 'Guayaquil', 'Asunción', 'San José', 'La Paz', 'Miami'];
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function weightedPick(products: Product[]): Product {
  const total = products.reduce((sum, p) => sum + p.weight, 0);
  let r = Math.random() * total;
  for (const p of products) {
    if (r < p.weight) return p;
    r -= p.weight;
  }
  return products[0];
}
export function RecentBookings() {
  const [current, setCurrent] = useState<Booking | null>(null);
  const recentNamesRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    let visibleTimer: ReturnType<typeof setTimeout> | null = null;
    let scheduleTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    const pickName = () => {
      const recent = recentNamesRef.current;
      const available = NAMES.filter(n => !recent.includes(n));
      const pool = available.length > 0 ? available : NAMES;
      const chosen = pickRandom(pool);
      recentNamesRef.current = [chosen, ...recent].slice(0, 10);
      return chosen;
    };
    const showOne = () => {
      if (cancelled) return;
      const product = weightedPick(PRODUCTS);
      const booking: Booking = {
        id: ++idRef.current,
        name: pickName(),
        city: pickRandom(CITIES),
        product
      };
      setCurrent(booking);
      visibleTimer = setTimeout(() => {
        if (cancelled) return;
        setCurrent(null);
        scheduleTimer = setTimeout(scheduleNext, 600);
      }, 4000);
    };
    const scheduleNext = () => {
      if (cancelled) return;
      const delay = 8000 + Math.random() * 7000; // 8s - 15s
      scheduleTimer = setTimeout(showOne, delay);
    };
    // First notification after 3s
    scheduleTimer = setTimeout(showOne, 3000);
    return () => {
      cancelled = true;
      if (visibleTimer) clearTimeout(visibleTimer);
      if (scheduleTimer) clearTimeout(scheduleTimer);
    };
  }, []);
  const ProductIcon = current?.product.Icon;
  return <div aria-live="polite" aria-atomic="true" className="fixed left-0 right-0 bottom-4 sm:bottom-6 z-30 pointer-events-none flex justify-center px-4" data-id="element-372">
      <AnimatePresence mode="wait" data-id="element-373">
        {current && ProductIcon && <motion.div key={current.id} initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 24
      }} animate={reduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 24
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white border border-teal/15 shadow-lg rounded-2xl pl-3 pr-4 py-3 flex items-start gap-3 max-w-[360px] w-full sm:w-auto" role="status" data-id="element-374">
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-teal/10 flex items-center justify-center mt-0.5" data-id="element-375">
              <ProductIcon className="text-teal" size={20} strokeWidth={1.75} data-id="element-376" />
            </div>

            <div className="flex-1 min-w-0" data-id="element-377">
              <div className="text-teal-dark font-medium text-sm leading-snug" data-id="element-378">
                {current.name} de {current.city}
              </div>
              <div className="text-gray-500 font-light text-xs italic" data-id="element-379">
                acaba de agendar una sesión
              </div>
              <div className="text-gray-700 text-xs mt-1 font-light truncate" data-id="element-380">
                <span className="font-serif text-teal-dark" data-id="element-381">
                  {current.product.name}
                </span>
                <span className="text-gray-400" data-id="element-382"> · </span>
                <span className="text-teal" data-id="element-383">{current.product.priceText}</span>
              </div>
              <div className="text-gray-400 text-[10px] uppercase tracking-wider mt-1" data-id="element-384">
                hace un momento
              </div>
            </div>

            <button onClick={() => setCurrent(null)} className="flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors -mr-1 mt-0.5" aria-label="Cerrar notificación" data-id="element-385">
              <X size={14} data-id="element-386" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}